// The update — two or three sentences the team writes, leading the page.
//
// Every other figure on Home comes from a formula. This is the one place the
// investment team says what the numbers mean, and it is the first thing a
// partner or an investor reads. Stored in app_settings under `home_update` as
// { text, by, at }, through the same upsert Settings uses, so it needs no table.
//
// In Present mode an empty update is simply absent — a prompt to write one is
// for the team, not for the room.
import { useEffect, useState } from "react";
import { supabase, updateAppSetting } from "../../supabase";

const KEY = "home_update";

export default function HomeUpdate({ present, authorName }) {
  const [update, setUpdate] = useState(null);     // { text, by, at }
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("app_settings").select("value").eq("key", KEY).maybeSingle();
      if (cancelled || !data?.value) return;
      try { setUpdate(JSON.parse(data.value)); } catch { setUpdate({ text: data.value }); }
    })();
    return () => { cancelled = true; };
  }, []);

  async function save() {
    const next = { text: draft.trim(), by: authorName || null, at: new Date().toISOString() };
    setSaving(true); setError(null);
    try {
      await updateAppSetting(KEY, JSON.stringify(next));
      setUpdate(next.text ? next : null);
      setEditing(false);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  const hasText = !!update?.text;
  if (present && !hasText) return null;

  return (
    <div data-print-block className="mb-4 rounded-r-xl border-l-[3px] border-[#e93529] bg-[#242424] px-5 py-3.5">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8a8a8a]">The update</div>

      {editing ? (
        <div className="mt-2">
          <textarea
            autoFocus
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What partners should take from this week, in two or three sentences."
            className="w-full resize-y rounded-lg border border-[#3a3a3a] bg-[#1c1c1c] px-3 py-2 text-[14px] leading-relaxed text-[#f4f4f4] placeholder-[#7a7a7a] focus:border-[#e93529] focus:outline-none"
          />
          <div className="mt-2 flex items-center gap-2">
            <button onClick={save} disabled={saving}
              className="h-8 rounded-md bg-[#e93529] px-3 text-[12px] font-semibold hover:bg-[#f04f44] disabled:opacity-60"
              style={{ color: "#ffffff" }}>
              {saving ? "Saving…" : "Save update"}
            </button>
            <button onClick={() => { setEditing(false); setError(null); }}
              className="h-8 rounded-md bg-[#2c2c2c] px-3 text-[12px] font-semibold text-[#b4b4b4] hover:bg-[#3a3a3a]">
              Cancel
            </button>
            {error && <span className="text-[11.5px] text-[#f04f44]">Couldn't save: {error}</span>}
          </div>
        </div>
      ) : hasText ? (
        <>
          <p className="mt-1.5 max-w-[900px] whitespace-pre-line text-[15px] leading-relaxed text-[#f4f4f4]">{update.text}</p>
          {!present && (
            <div data-print-hide className="mt-2 text-[11px] text-[#7a7a7a]">
              {update.by ? `Written by ${update.by}` : "Written by the team"}
              {update.at ? ` · ${new Date(update.at).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}` : ""}
              {" · "}
              <button onClick={() => { setDraft(update.text); setEditing(true); }} className="text-[#e93529] hover:text-[#f04f44]">Edit</button>
            </div>
          )}
        </>
      ) : (
        <button data-print-hide onClick={() => { setDraft(""); setEditing(true); }}
          className="mt-1.5 text-[13.5px] text-[#8a8a8a] hover:text-[#f4f4f4]">
          + Write this week's update for the partners
        </button>
      )}
    </div>
  );
}
